import type { Campaign } from "@/lib/core/types";

export type LaunchCampaignInput = {
  characterId: string;
  name: string;
  systemInstructions?: string;
};

/**
 * Structured failure from `launchCampaign`, carrying the kernel ack's
 * `data` fields (see `launch_campaign_with_character_5e`'s `fail()` helper
 * in `lib/rules/dnd5e/character/tools/launch-campaign-with-character.ts`)
 * so callers can branch on `variant` / `existingCampaignId` /
 * `existingActorId` instead of parsing `message` text.
 */
export class LaunchCampaignError extends Error {
  readonly variant: string;
  readonly existingCampaignId?: string;
  readonly existingActorId?: string;

  constructor(
    message: string,
    variant: string,
    options?: { existingCampaignId?: string; existingActorId?: string }
  ) {
    super(message);
    this.name = "LaunchCampaignError";
    this.variant = variant;
    this.existingCampaignId = options?.existingCampaignId;
    this.existingActorId = options?.existingActorId;
  }
}

export async function launchCampaign(
  input: LaunchCampaignInput
): Promise<{ campaign: Campaign; characterId: string; actorId: string }> {
  const res = await fetch("/api/account/campaigns/launch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body = await res.json();
  if (!res.ok) {
    const variant = body?.data?.variant;
    const existingCampaignId = body?.data?.existingCampaignId;
    const existingActorId = body?.data?.existingActorId;
    const message =
      typeof body?.error === "string"
        ? body.error
        : "Failed to launch campaign";
    throw new LaunchCampaignError(
      message,
      typeof variant === "string" ? variant : "unknown",
      {
        existingCampaignId:
          typeof existingCampaignId === "string"
            ? existingCampaignId
            : undefined,
        existingActorId:
          typeof existingActorId === "string" ? existingActorId : undefined,
      }
    );
  }
  return body;
}

export async function archiveCampaign(id: string): Promise<void> {
  const res = await fetch(`/api/campaigns/${id}/archive`, { method: "POST" });
  if (!res.ok) {
    const { error } = await res.json();
    throw new Error(error ?? "Failed to archive campaign");
  }
}

export async function fetchCampaign(id: string): Promise<Campaign | null> {
  const res = await fetch(`/api/campaigns/${id}`);
  if (res.status === 404) {
    return null;
  }
  if (res.status === 409) {
    throw new Error("campaign_archived");
  }
  if (!res.ok) {
    throw new Error("Failed to fetch campaign");
  }
  return res.json();
}
